import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ButtonSpinner } from '../components/ui/Loader';
import {
  useRequestTelegramLoginCodeMutation,
} from '../store/api/authApi';

interface FormErrors {
  telegramId?: string;
}

export function TelegramLoginRequestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requestCode, { isLoading }] = useRequestTelegramLoginCodeMutation();

  const [formData, setFormData] = useState({
    telegramId: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [touched, setTouched] = useState({
    telegramId: false,
  });

  const validateTelegramId = (id: string): string | undefined => {
    if (!id.trim()) {
      return t('auth.telegramLogin.telegramIdRequired');
    }
    const numId = Number.parseInt(id, 10);
    if (Number.isNaN(numId) || numId <= 0) {
      return t('auth.telegramLogin.telegramIdInvalid');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const telegramIdError = validateTelegramId(formData.telegramId);
    if (telegramIdError) newErrors.telegramId = telegramIdError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    setTouched({ telegramId: true });

    if (!validateForm()) {
      return;
    }

    try {
      await requestCode({
        telegramId: Number.parseInt(formData.telegramId, 10),
      }).unwrap();

      // Navigate to verification page with telegramId
      navigate(`/telegram-login-verify?telegramId=${formData.telegramId}`);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setLocalError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }

    if (touched[name as keyof typeof touched]) {
      if (name === 'telegramId') {
        const error = validateTelegramId(value);
        setErrors((prev) => ({
          ...prev,
          telegramId: error,
        }));
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === 'telegramId') {
      const error = validateTelegramId(value);
      setErrors((prev) => ({
        ...prev,
        telegramId: error,
      }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">

      <div className="w-full max-w-md animate-scale-in relative z-10">
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          <div className="text-center space-y-2 animate-slide-down">
            <div className="flex justify-center mb-2">
              <svg
                className="w-14 h-14 text-brand-accent"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.12l-6.87 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
              </svg>
            </div>
            <h1 className="text-4xl font-heading font-bold text-brand-accent">
              {t('auth.telegramLogin.title')}
            </h1>
            <p className="text-neutral-medium font-body">
              {t('auth.telegramLogin.subtitle')}
            </p>
          </div>

          {(localError) && (
            <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
              <p className="text-red-300 text-sm font-body">{localError}</p>
            </div>
          )}

          <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-5">
            <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <label
                htmlFor="telegramId"
                className="block text-sm font-body font-medium text-neutral-light"
              >
                {t('auth.telegramLogin.telegramId')}
              </label>
              <input
                type="text"
                id="telegramId"
                name="telegramId"
                value={formData.telegramId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body ${
                  errors.telegramId && touched.telegramId
                    ? 'border-danger focus:ring-danger focus:border-danger'
                    : 'border-neutral-dark focus:border-brand-accent'
                }`}
                placeholder={t('auth.telegramLogin.telegramIdPlaceholder')}
              />
              {errors.telegramId && touched.telegramId && (
                <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                  {errors.telegramId}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-slide-up flex items-center justify-center gap-2"
              style={{ animationDelay: '0.2s' }}
            >
              {isLoading ? (
                <>
                  <ButtonSpinner />
                  <span>{t('common.loading')}</span>
                </>
              ) : (
                t('auth.telegramLogin.requestCode')
              )}
            </button>
          </form>

          <div className="text-center animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-body"
            >
              {t('common.back')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

